/**
 * Alshallal HR — Login Page
 *
 * Supports email, National ID, or Iqama login.
 * Brand: Beige main, Orange accent, Black wordmark, Brown CTAs.
 */
import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Loader2, ChevronRight, ChevronLeft } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { AlshallalLogo } from '@/components/brand/AlshallalLogo';
import { OtpChallengeForm } from '@/components/auth/OtpChallengeForm';
import { OtpSetupForm } from '@/components/auth/OtpSetupForm';

/**
 * Where a freshly signed-in person belongs. The Confidentiality Undertaking
 * comes first: the server 403s every other endpoint until it is accepted, so
 * routing them to /change-password or /dashboard first would land them on a
 * screen that cannot load.
 */
const landingFor = (u) => {
  if (u?.nda_required) return '/nda';
  if (u?.must_change_password) return '/change-password';
  return '/dashboard';
};

const LoginPage = () => {
  const navigate = useNavigate();
  const {
    login, verifyOtp, resendOtp, startOtpSetup, confirmOtpSetup, skipOtpSetup,
    isAuthenticated, loading: authLoading, user,
  } = useAuth();
  const { t, language, toggleLanguage, isRTL } = useLanguage();

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Set when the server answers "password accepted, now the code". Holds the
  // challenge only - no password, and no token yet.
  const [otpChallenge, setOtpChallenge] = useState(null);
  // Set when a Super Admin has required two-step sign-in for this account and
  // it has not been set up yet. Holds the half-authenticated setup ticket.
  const [otpSetup, setOtpSetup] = useState(null);
  // M13: set by the axios interceptor in AuthContext when the server ends a
  // session (logout elsewhere, password change, admin lock, idle timeout).
  // Read once and cleared, so it never shows up on a later visit.
  const [signedOutReason] = useState(() => {
    const reason = localStorage.getItem('taqueen_signed_out_reason');
    if (reason) localStorage.removeItem('taqueen_signed_out_reason');
    return reason;
  });

  // Redirect if already authenticated. Suppressed during first-time setup:
  // `confirmOtpSetup` signs the person in, and redirecting on that render would
  // skip the backup codes, which are shown exactly once.
  if (isAuthenticated && !authLoading && !otpSetup) {
    return <Navigate to={landingFor(user)} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(identifier, password);

      if (result.otpRequired) {
        setPassword('');
        setOtpChallenge(result);
        return;
      }

      if (result.otpEnrollmentRequired) {
        setPassword('');
        setOtpSetup(result);
        return;
      }

      if (result.success) {
        navigate(landingFor(result.user));
      } else {
        setError(result.error || t('error_occurred'));
      }
    } catch (err) {
      setError(t('error_occurred'));
    } finally {
      setLoading(false);
    }
  };

  const handleOtpVerified = (result) => {
    navigate(landingFor(result.user));
  };

  // Called from the backup-codes screen, once the person says they have saved
  // them. They are already signed in by this point.
  const handleSetupComplete = () => {
    setOtpSetup(null);
    navigate(landingFor(user));
  };

  const SubmitIcon = isRTL ? ChevronLeft : ChevronRight;

  return (
    <div className="min-h-screen flex bg-background" data-testid="login-page">
      {/* Left side — Hero (hidden on mobile) */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden bg-brand-black">
        {/* Animated gradient blobs */}
        <motion.div
          aria-hidden="true"
          className="absolute -top-32 -left-32 h-96 w-96 rounded-full"
          style={{ background: 'radial-gradient(closest-side, rgba(209,128,47,0.55), transparent)' }}
          animate={{ x: [0, 30, 0], y: [0, 20, 0] }}
          transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          aria-hidden="true"
          className="absolute -bottom-32 -right-24 h-[28rem] w-[28rem] rounded-full"
          style={{ background: 'radial-gradient(closest-side, rgba(186,217,235,0.30), transparent)' }}
          animate={{ x: [0, -25, 0], y: [0, -15, 0] }}
          transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          aria-hidden="true"
          className="absolute top-1/3 right-1/4 h-64 w-64 rounded-full"
          style={{ background: 'radial-gradient(closest-side, rgba(143,52,12,0.40), transparent)' }}
          animate={{ scale: [1, 1.15, 1] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        />

        {/* Subtle grain */}
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.06] mix-blend-overlay"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
          }}
        />

        <div className="relative z-10 flex flex-col justify-between w-full p-12 text-brand-beige">
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
          >
            <AlshallalLogo size="lg" variant="on-dark" showWordmark wordmarkAr={isRTL} animated />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.15 }}
            className="max-w-md"
          >
            <h2 className="text-5xl font-bold leading-tight tracking-tight mb-5">
              {isRTL ? (
                <>
                  {'منصة الموارد البشرية'}
                  <span className="block text-brand-orange mt-2">{'شركة معجنات الشلال'}</span>
                </>
              ) : (
                <>
                  Human Resources, <br/>
                  <span className="text-brand-orange">elevated.</span>
                </>
              )}
            </h2>
            <p className="text-brand-beige/70 text-lg leading-relaxed">
              {isRTL
                ? 'إدارة الموظفين، الإجازات، الطلبات والخطابات في مكان واحد — بأسلوب احترافي.'
                : 'Employees, leave, requests and letters — unified in one elegant workspace.'}
            </p>

            <div className="mt-10 flex items-center gap-6 text-sm text-brand-beige/65">
              <div className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-brand-orange animate-pulse"></span>
                {isRTL ? 'آمن ومحمي' : 'Secure'}
              </div>
              <div className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-brand-sky animate-pulse"></span>
                {isRTL ? 'ثنائي اللغة' : 'Bilingual'}
              </div>
              <div className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-brand-brown animate-pulse"></span>
                {isRTL ? 'سحابي' : 'Cloud-native'}
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.4 }}
            className="text-xs text-brand-beige/45"
          >
            &copy; {new Date().getFullYear()} Alshallal Pastry Company — All rights reserved.
          </motion.div>
        </div>
      </div>

      {/* Right side — Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12 relative">
        {/* Mobile-only top logo */}
        <div className="lg:hidden absolute top-10 left-1/2 -translate-x-1/2">
          <AlshallalLogo size="xl" animated />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md mt-44 lg:mt-0"
        >
          {/* Language toggle */}
          <div className="flex justify-end mb-8">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              className="text-brand-brown hover:text-brand-black hover:bg-brand-beige-60"
              data-testid="login-language-toggle"
            >
              {language === 'en' ? 'العربية' : 'English'}
            </Button>
          </div>

          {otpSetup ? (
            <OtpSetupForm
              setup={otpSetup}
              isRTL={isRTL}
              startSetup={startOtpSetup}
              confirmSetup={confirmOtpSetup}
              skipSetup={skipOtpSetup}
              onComplete={handleSetupComplete}
            />
          ) : otpChallenge ? (
            <OtpChallengeForm
              challenge={otpChallenge}
              isRTL={isRTL}
              verifyOtp={verifyOtp}
              resendOtp={resendOtp}
              onVerified={handleOtpVerified}
              onCancel={() => { setOtpChallenge(null); setError(''); }}
            />
          ) : (
          <>
          {/* Heading */}
          <div className="mb-8">
            <motion.h1
              initial={{ opacity: 0, x: isRTL ? 10 : -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="text-4xl font-bold text-brand-black tracking-tight"
            >
              {isRTL ? 'مرحباً بعودتك' : 'Welcome back'}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.2 }}
              className="text-muted-foreground mt-2"
            >
              {t('login_subtitle')}
            </motion.p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* M13: why the person is suddenly back here. Without this, an
                inactivity timeout or an admin revoking their session looks
                like the app randomly logged them out. */}
            {signedOutReason && !error && (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-3 rounded-lg text-sm font-medium border"
                style={{
                  background: 'hsl(38 92% 40% / 0.10)',
                  borderColor: 'hsl(38 92% 40% / 0.32)',
                  color: 'hsl(30 70% 24%)',
                }}
                data-testid="login-signed-out-notice"
              >
                {signedOutReason === 'idle'
                  ? t('signed_out_idle')
                  : signedOutReason === 'locked'
                  ? t('signed_out_locked')
                  : signedOutReason === 'inactive'
                  ? t('signed_out_inactive')
                  : t('signed_out_revoked')}
              </motion.div>
            )}

            {/* Error message */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-3 rounded-lg text-sm font-medium border"
                style={{
                  background: 'hsl(18 84% 30% / 0.08)',
                  borderColor: 'hsl(18 84% 30% / 0.30)',
                  color: 'hsl(18 84% 24%)',
                }}
                data-testid="login-error"
              >
                {error}
              </motion.div>
            )}

            {/* Identifier field */}
            <div className="space-y-2">
              <Label htmlFor="identifier" className="text-brand-black font-medium">
                {t('email')} / {t('national_id')} / {t('iqama')}
              </Label>
              <Input
                id="identifier"
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder={t('identifier_placeholder')}
                required
                disabled={loading}
                className="h-12 bg-card border-border focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-all"
                data-testid="login-identifier"
                dir="ltr"
              />
            </div>

            {/* Password field */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-brand-black font-medium">
                  {t('password')}
                </Label>
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-sm font-medium text-brand-brown hover:text-brand-orange transition-colors"
                  data-testid="forgot-password-link"
                >
                  {t('forgot_password')}
                </button>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  disabled={loading}
                  className="h-12 pe-10 bg-card border-border focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-all"
                  data-testid="login-password"
                  dir="ltr"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={cn(
                    'absolute top-1/2 -translate-y-1/2 text-muted-foreground hover:text-brand-brown transition-colors',
                    isRTL ? 'left-3' : 'right-3'
                  )}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Submit button */}
            <motion.button
              type="submit"
              disabled={loading}
              whileHover={!loading ? { y: -1 } : {}}
              whileTap={!loading ? { scale: 0.98 } : {}}
              className={cn(
                'w-full h-12 rounded-lg font-semibold text-brand-beige text-base inline-flex items-center justify-center gap-2',
                'bg-brand-black hover:bg-[#2A2624] shadow-brand-md transition-colors',
                'focus:outline-none focus:ring-4 focus:ring-brand-orange/30',
                'disabled:opacity-60 disabled:cursor-not-allowed'
              )}
              data-testid="login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('loading')}
                </>
              ) : (
                <>
                  {t('login')}
                  <SubmitIcon className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </>
              )}
            </motion.button>
          </form>
          </>
          )}

          {/* Footer */}
          <div className="mt-10 text-center text-xs text-muted-foreground">
            <p>
              &copy; {new Date().getFullYear()} Alshallal Pastry Company &middot;{' '}
              {isRTL ? 'جميع الحقوق محفوظة' : 'All rights reserved'}
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;
